import {
  Children,
  createContext,
  forwardRef,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn'
import { toLatinDigits } from '../lib/date'

/* ------------------------------------------------------------------
   Field — پوشش مشترک برچسب، خطا، راهنما و اتصال aria
   سند مادر بند ۱۵: خطای Validation شامل field/code/message است؛
   این کامپوننت مصرف‌کننده همان ساختار است.
   ------------------------------------------------------------------ */

interface FieldContextValue {
  id: string
  describedBy?: string
  invalid: boolean
}

const FieldContext = createContext<FieldContextValue | null>(null)

export interface FieldProps {
  label?: ReactNode
  hint?: ReactNode
  /** پیام خطای سمت سرور یا فرم */
  error?: string | null
  required?: boolean
  /**
   * نشانِ «اختیاری» کنار برچسب.
   *
   * 🔴 چرا لازم است: در فرمی که بیشترِ فیلدهایش اجباری‌اند، ستارهٔ قرمز
   * قاعده می‌شود و نبودنش استثنا — ولی «نبودنِ» یک نشانه چیزی نیست که
   * چشم بگیرد. فرم‌ها به‌جایش `hint="اختیاری"` می‌نوشتند که آن را زیرِ
   * فیلد و هم‌شکلِ راهنمای واقعی می‌گذاشت، و جای `hint` را هم اشغال
   * می‌کرد.
   *
   * ⚠️ با `required` جمع نمی‌شود؛ اگر هر دو داده شود `required` برنده است.
   */
  optional?: boolean
  children: ReactNode
  className?: string
}

export function Field({
  label,
  hint,
  error,
  required,
  optional,
  children,
  className,
}: FieldProps) {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ')

  return (
    <FieldContext.Provider value={{ id, describedBy: describedBy || undefined, invalid: !!error }}>
      <div className={cn('flex flex-col gap-1.5', className)}>
        {label && (
          <label
            htmlFor={id}
            className="flex items-center gap-1 text-[length:var(--pf-text-sm)] font-[var(--pf-weight-medium)] text-[var(--pf-text)]"
          >
            {label}
            {required && (
              <span className="text-[var(--pf-danger-text)]" aria-hidden="true">
                *
              </span>
            )}
            {/*
              ⚠️ برخلاف ستاره، `aria-hidden` نیست: ستاره قراردادی است که
              صفحه‌خوان از `required` خودِ کنترل هم می‌فهمد، ولی
              «اختیاری» هیچ معادلِ برنامه‌ای ندارد و اگر پنهان شود
              کاربرِ صفحه‌خوان اصلاً آن را نمی‌شنود.
            */}
            {optional && !required && (
              <span className="font-[var(--pf-weight-normal)] text-[length:var(--pf-text-xs)] text-[var(--pf-text-subtle)]">
                (اختیاری)
              </span>
            )}
          </label>
        )}

        {children}

        {hint && !error && (
          <p id={hintId} className="text-[length:var(--pf-text-xs)] text-[var(--pf-text-muted)]">
            {hint}
          </p>
        )}

        {error && (
          <p
            id={errorId}
            role="alert"
            className="text-[length:var(--pf-text-xs)] text-[var(--pf-danger-text)]"
          >
            {error}
          </p>
        )}
      </div>
    </FieldContext.Provider>
  )
}

/**
 * زمینه فیلد — برچسب، شناسه و وضعیت نامعتبربودن.
 *
 * ⚠️ صادر می‌شود تا کنترل‌های سفارشی (مثل `JalaliDatePicker`) هم بتوانند
 * همان رفتار `Input` را داشته باشند: گرفتن `id` از برچسب و قرمزشدن
 * هنگام خطا. بدون این، هر کنترل تازه باید خودش دوباره سیم‌کشی شود و
 * دیر یا زود یکی‌شان فراموش می‌شود.
 */
export function useField() {
  return useContext(FieldContext)
}

/* ------------------------------------------------------------------
   استایل مشترک کنترل‌ها — مدل «Filled»
   ------------------------------------------------------------------
   🔴 کنترل‌ها قاب کامل ندارند: زمینهٔ ملایم + یک خط زیرین.

   پیش‌تر مدل Outlined بود (حاشیهٔ ۱px دور تا دور، زمینهٔ سفید). در
   فرمی مثل ثبت مرخصی با هفت فیلد، هفت مستطیلِ کامل روی کارتِ سفید
   می‌نشست و خودِ کادرها پیش از محتوا دیده می‌شدند. با زمینهٔ ملایم،
   مرزِ فیلد از **کنتراستِ سطح** می‌آید و نه از خط — سبک‌تر دیده
   می‌شود بی‌آنکه محدودهٔ کلیک مبهم شود.

   ⚠️ خط زیرین حذف نشد. با زمینهٔ تنها، فیلدِ غیرفعال و فیلدِ خالی و
   کارتِ خاکستریِ پشتشان سه سطحِ نزدیک به هم می‌شدند؛ خطِ پایین همان
   «کفِ» فیلد را تعریف می‌کند و در حالت فوکوس هم چیزی هست که پررنگ
   شود.

   🔴 چرا `border` روی هر چهار طرف می‌ماند و فقط رنگش شفاف می‌شود:
   اگر `border-b` تنها به‌کار می‌رفت، ارتفاعِ کنترل ۱px کمتر از
   Outlined می‌شد و همهٔ کنترل‌های هم‌ردیف (دکمه، تقویم) یک پیکسل
   جابه‌جا می‌شدند. با حاشیهٔ شفاف، جعبه دقیقاً همان اندازه می‌ماند.

   ⚠️ در حالت فوکوس، خطِ زیرین سبز و ۲px می‌شود ولی حلقهٔ فوکوسِ
   سراسری (`:focus-visible` در tokens.css) هم سرِ جایش می‌ماند —
   خطِ زیرین تزئین است و آنچه دسترس‌پذیری را تأمین می‌کند حلقه است.
   ------------------------------------------------------------------ */

const controlBase = (invalid: boolean) =>
  cn(
    'w-full text-[var(--pf-text)]',
    /* گوشه‌های پایین صاف‌تر است تا خطِ زیرین تا لبه ادامه پیدا کند. */
    'rounded-t-[var(--pf-radius)] rounded-b-[var(--pf-radius-sm)]',
    'px-3 text-[length:var(--pf-text-md)]',
    'transition-[background-color,border-color] duration-[var(--pf-duration-fast)]',
    'placeholder:text-[var(--pf-text-subtle)]',
    /*
      حاشیهٔ شفاف جای جعبه را نگه می‌دارد؛ فقط خطِ پایین دیده می‌شود.

      ⚠️ سه طرف صریح شفاف می‌شوند و نه با `border-transparent` سراسری،
      تا با `border-b-*` بر سر `border-color` رقابت نکنند.
    */
    'border border-x-transparent border-t-transparent',
    'disabled:cursor-not-allowed disabled:opacity-60',
    invalid
      ? cn(
          'bg-[var(--pf-danger-50)] border-b-2 border-b-[var(--pf-danger-500)]',
          'focus-visible:border-b-[var(--pf-danger-500)]',
          /* ⚠️ قرمزشدنِ حلقهٔ فوکوس اینجا نیست: قاعدهٔ
             `:focus-visible[aria-invalid='true']` در `tokens.css` آن
             را می‌دهد. کلاسِ تیلویند آنجا بی‌اثر است چون قاعدهٔ
             بی‌لایه بر ابزارِ لایه‌دار می‌چربد. */
        )
      : cn(
          /*
            🔴 خطِ زیرین ۲px است و رنگش `--pf-text-subtle` — نه ۱px و
            نه `--pf-border-strong`.

            ⚠️ نسخهٔ اول ۱px با `--pf-border-strong` (#c3cad2) بود. در
            آزمون مرورگر با بزرگ‌نماییِ ۳ برابر معلوم شد که CSS درست
            اعمال می‌شود ولی خط **دیده نمی‌شود**: #c3cad2 روی زمینهٔ
            #f2f4f6 فیلد تفاوتِ محسوسی ندارد، و کنارِ آن زمینهٔ خاکستری
            که خودش مرزِ فیلد را می‌سازد، یک خطِ کم‌رنگ‌تر عملاً گم
            می‌شود. یعنی مدل عملاً «Filled بدون خط» شده بود.

            ۲px با رنگِ متنِ کم‌رنگ، همان لنگرِ پایینی را می‌دهد که مدل
            Filled به آن نیاز دارد و در حالت فوکوس هم واقعاً «پررنگ‌تر»
            شدن دیده می‌شود.
          */
          'bg-[var(--pf-surface-muted)] border-b-2 border-b-[var(--pf-text-subtle)]',
          'hover:bg-[var(--pf-surface-hover)]',
          'focus-visible:border-b-[var(--pf-primary-container)]',
          /* ⚠️ `focus-within` برای کنترل‌های مرکب (مثل ورودیِ داخل
             پوششِ آیکون‌دار) لازم است؛ خودِ عنصر آنجا فوکوس نمی‌گیرد. */
          'focus-within:border-b-[var(--pf-primary-container)]',
        ),
  )

/* ------------------------------------------------------------------
   Input
   ------------------------------------------------------------------ */

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  invalid?: boolean
  /** ارقام فارسی ورودی را پیش از onChange به لاتین تبدیل می‌کند */
  normalizeDigits?: boolean
  prefix?: ReactNode
  suffix?: ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, normalizeDigits, prefix, suffix, className, onChange, dir, ...rest },
  ref,
) {
  const field = useField()
  const isInvalid = invalid ?? field?.invalid ?? false

  const control = (
    <input
      ref={ref}
      id={field?.id}
      aria-invalid={isInvalid || undefined}
      aria-describedby={field?.describedBy}
      dir={dir}
      onChange={(event) => {
        if (normalizeDigits) {
          const latin = toLatinDigits(event.target.value)
          if (latin !== event.target.value) event.target.value = latin
        }
        onChange?.(event)
      }}
      className={cn(
        controlBase(isInvalid),
        'h-9.5',
        /* 🔴 این دو کلاس عمداً به جای ps/pe از متغیر CSS استفاده
           می‌کنند. فیلد ایمیل/موبایل/رمز در index.css به‌زور
           direction:ltr می‌گیرد تا مقدارش چپ‌به‌راست خوانده شود؛ با
           این کار «آغاز» و «پایان» منطقیِ خودِ input هم برمی‌گردند،
           در حالی که پوششِ آیکون از جهتِ صفحه پیروی می‌کند. نتیجه:
           جای خالی در سمتِ مخالفِ آیکون باز می‌شد و متن روی آیکون
           می‌افتاد. با padding-inline رویِ پوشش (نه input) هر دو از
           یک جهت پیروی می‌کنند. */
        prefix && 'pf-affix-start',
        suffix && 'pf-affix-end',
        className,
      )}
      {...rest}
    />
  )

  if (!prefix && !suffix) return control

  return (
    <div className="relative">
      {prefix && (
        <span className="absolute inset-y-0 start-0 flex items-center ps-3 text-[var(--pf-text-subtle)] pointer-events-none">
          {prefix}
        </span>
      )}
      {control}
      {suffix && (
        <span className="absolute inset-y-0 end-0 flex items-center pe-3 text-[var(--pf-text-subtle)]">
          {suffix}
        </span>
      )}
    </div>
  )
})

/* ------------------------------------------------------------------
   Textarea
   ------------------------------------------------------------------ */

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, className, rows = 4, ...rest },
  ref,
) {
  const field = useField()
  const isInvalid = invalid ?? field?.invalid ?? false

  return (
    <textarea
      ref={ref}
      id={field?.id}
      rows={rows}
      aria-invalid={isInvalid || undefined}
      aria-describedby={field?.describedBy}
      className={cn(controlBase(isInvalid), 'py-2 resize-y leading-[var(--pf-leading-normal)]', className)}
      {...rest}
    />
  )
})

/* ------------------------------------------------------------------
   Select
   ------------------------------------------------------------------ */

export interface SelectOption {
  value: string | number
  label: string
  disabled?: boolean
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  invalid?: boolean
  options?: SelectOption[]
  placeholder?: string
  /** عنوان کشوی موبایل — پیش‌فرض برچسبِ همان فیلد است */
  title?: string
}

/**
 * زیر این عرض، فهرست به‌جای پنجره شناور، کشوی پایین صفحه می‌شود.
 *
 * ⚠️ همان نقطه‌ای است که `sm:` تیلویند دارد. عدد اینجا تکرار شده چون
 * تصمیم باید در JS گرفته شود (کدام گره در DOM برود) و نه با کلاس —
 * رندرِ هر دو نسخه و پنهان‌کردن یکی با CSS یعنی دو فهرست در درخت
 * دسترس‌پذیری و دو بار پیمایش با Tab.
 */
const DRAWER_BREAKPOINT = 640

/** فاصله پنجره شناور از لبه صفحه، تا به کف/سقف نچسبد. */
const VIEWPORT_MARGIN = 8

/**
 * مدت انیمیشن خروجِ کشو — باید با `pf-drawer-out` در `tokens.css` یکی بماند.
 *
 * ⚠️ عدد اینجا تکرار شده چون JS باید بداند کِی گره را بردارد. اگر از
 * توکن CSS خوانده می‌شد، در حالتِ «کاهش حرکت» که مرورگر مدت را صفر
 * می‌کند، این عدد همچنان قدیمی می‌ماند.
 */
const DRAWER_EXIT_MS = 200

/**
 * تبدیل `<option>`های فرزند به داده.
 *
 * 🔴 چرا لازم است: چند فرم به‌جای `options` بچه‌ی `<option>` می‌دادند —
 * نحوی که با `<select>` بومی کار می‌کرد. حالا که فهرست، دکمه است و نه
 * `<option>`، آن گره‌ها دیگر رندرشدنی نیستند. به‌جای شکستنِ بی‌صدای آن
 * فرم‌ها (فهرستِ خالی، بدون خطا) همان‌جا خوانده و به داده تبدیل می‌شوند.
 */
function optionsFromChildren(children: ReactNode): SelectOption[] {
  const collected: SelectOption[] = []

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return

    const props = child.props as {
      value?: string | number
      disabled?: boolean
      children?: ReactNode
    }

    if (child.type === 'option') {
      collected.push({
        value: props.value ?? '',
        label: typeof props.children === 'string' ? props.children : String(props.children ?? ''),
        disabled: props.disabled,
      })
      return
    }

    /* `<optgroup>` و قطعه‌ها — یک لایه تودرتو باز می‌شود. */
    collected.push(...optionsFromChildren(props.children))
  })

  return collected
}

/**
 * انتخابگر — پنجره شناور در دسکتاپ، کشو در موبایل.
 *
 * ------------------------------------------------------------------
 * 🔴 چرا `<select>` بومی کنار گذاشته شد:
 *
 * منوی بومی را نمی‌شود استایل داد. در ویندوز و اندروید فهرست با فونت و
 * رنگ و گوشه‌های سیستم‌عامل باز می‌شد — نه فونت فارسی برنامه، نه
 * توکن‌های پوسته، و در پوسته تاریک یک مستطیل سفیدِ روشن وسط صفحه تیره.
 *
 * ⚠️ قرارداد `onChange` عمداً همان قرارداد بومی ماند: مصرف‌کننده هنوز
 * `e.target.value` می‌خواند. ۴۹ فراخوان در برنامه هست؛ عوض‌کردن امضا
 * یعنی دست‌زدن به همه‌شان، بی‌آنکه چیزی در ازایش بدهد.
 *
 * ------------------------------------------------------------------
 * ⚠️ یک `<select>` پنهان در DOM می‌ماند. ظاهر از آنِ فهرست سفارشی است،
 * ولی فرمِ بومی، `FormData` و بازیابی خودکار مرورگر همچنان مقدار را
 * از همان می‌خوانند — و `ref` هم به همان می‌رسد، چون امضای بیرونی
 * `HTMLSelectElement` است.
 * ------------------------------------------------------------------ */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    invalid,
    options,
    placeholder,
    title,
    className,
    children,
    value,
    defaultValue,
    onChange,
    disabled,
    name,
    required,
    ...rest
  },
  ref,
) {
  const field = useField()
  const isInvalid = invalid ?? field?.invalid ?? false

  const items = useMemo(
    () => (options && options.length > 0 ? options : optionsFromChildren(children)),
    [options, children],
  )

  const triggerRef = useRef<HTMLButtonElement>(null)
  const selectRef = useRef<HTMLSelectElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const [open, setOpenState] = useState(false)
  const [isDrawer, setIsDrawer] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(null)

  /*
   | ⚠️ آینه‌ی `isDrawer` برای خواندن داخل `setOpen`.
   |
   | خودِ `setOpen` وابستگی ندارد تا مرجعش ثابت بماند (در فهرست
   | وابستگیِ چند Effect هست و با هر بار عوض‌شدن، شنونده‌ها بی‌دلیل
   | برداشته و دوباره نصب می‌شدند). پس مقدارِ تازه باید از ref بیاید و
   | نه از closure.
   */
  const isDrawerRef = useRef(false)
  isDrawerRef.current = isDrawer

  /*
   | فاز «در حال بسته‌شدن» — گره در DOM می‌ماند تا انیمیشن خروج بازی کند.
   |
   | 🔴 بدون این، `setOpen(false)` گره را همان لحظه از درخت برمی‌داشت و
   | کشو **می‌پرید** بیرون؛ انیمیشنی که روی گرهِ حذف‌شده تعریف شود هرگز
   | دیده نمی‌شود.
   |
   | ⚠️ فقط برای کشو معنا دارد. پنجره شناور دسکتاپ با اسکرول بسته
   | می‌شود و نگه‌داشتنِ ۲۰۰ میلی‌ثانیه‌ایِ آن، فهرستی جامانده کنار
   | دکمه‌ای که دیگر آنجا نیست نشان می‌داد.
   */
  const [closing, setClosing] = useState(false)
  const closeTimer = useRef<number | null>(null)

  /**
   | بستن — در کشو با انیمیشن، در دسکتاپ بی‌درنگ.
   |
   | ⚠️ همه‌ی جاهای بستن (Escape، کلیک بیرون، انتخاب گزینه، اسکرول،
   | دکمه بستن) از همین می‌گذرند تا رفتار یکی بماند.
   */
  const setOpen = useCallback((next: boolean) => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current)
      closeTimer.current = null
    }

    if (next) {
      setClosing(false)
      setOpenState(true)
      return
    }

    /*
     | ⚠️ در دسکتاپ (و وقتی کاربر «حرکت کمتر» خواسته) بی‌درنگ بسته
     | می‌شود. قاعده‌ی سراسریِ `prefers-reduced-motion` در `tokens.css`
     | مدت انیمیشن را صفر می‌کند؛ اگر تایمرِ ۲۰۰ms اینجا می‌ماند، کشو
     | فوراً نامرئی می‌شد ولی گره‌اش یک‌پنجم ثانیه جلوی کلیک را
     | می‌گرفت.
     */
    const instant =
      !isDrawerRef.current ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (instant) {
      setClosing(false)
      setOpenState(false)
      return
    }

    /* کشو: گره می‌ماند، پرچمِ خروج بالا می‌رود و تایمر جمعش می‌کند. */
    setClosing(true)
    closeTimer.current = window.setTimeout(() => {
      setClosing(false)
      setOpenState(false)
      closeTimer.current = null
    }, DRAWER_EXIT_MS)
  }, [])

  /* تایمرِ نیمه‌کاره نباید پس از unmount شلیک کند. */
  useEffect(
    () => () => {
      if (closeTimer.current !== null) window.clearTimeout(closeTimer.current)
    },
    [],
  )

  /*
   | مقدارِ کنترل‌نشده هم پشتیبانی می‌شود.
   |
   | ⚠️ بعضی فرم‌ها `defaultValue` می‌دهند و مقدار را از خودِ DOM
   | می‌خوانند. اگر فقط `value` خوانده می‌شد، آن‌ها همیشه خالی
   | می‌ماندند.
   */
  const [uncontrolled, setUncontrolled] = useState(() => defaultValue ?? '')
  const isControlled = value !== undefined
  const current = isControlled ? value : uncontrolled

  const selected = items.find((option) => String(option.value) === String(current ?? ''))

  /* گزینه‌ای که با کلید بالا/پایین روی آن ایستاده‌ایم. */
  const [activeIndex, setActiveIndex] = useState(-1)

  const listboxId = useId()
  const controlId = field?.id

  /*
   | ویژگی‌های اضافی به دکمه می‌رسند و نه به `<select>` پنهان.
   |
   | 🔴 هر چه مصرف‌کننده می‌دهد — `data-*`، `title`، `aria-label` — برای
   | عنصری است که کاربر می‌بیند و با آن کار می‌کند؛ روی گره پنهانِ
   | `aria-hidden` بی‌اثر می‌ماند.
   |
   | ⚠️ ولی رویدادها (`on…`) کنار گذاشته می‌شوند. امضای همه‌شان
   | `HTMLSelectElement` است و روی `<button>` نه از نظر نوع درست است و
   | نه از نظر رفتار — دکمه، `<select>` نیست. جداکردنشان در سطح **نوع**
   | انجام می‌شود و نه با شمردنِ نامشان، وگرنه هر رویدادی که از قلم
   | بیفتد دوباره همان خطا را می‌سازد.
   */
  const passthrough = Object.fromEntries(
    Object.entries(rest).filter(([key]) => !/^on[A-Z]/.test(key)),
  ) as Omit<typeof rest, `on${string}`>

  /*
   | رویداد به همان شکلِ بومی ساخته می‌شود.
   |
   | 🔴 مقدار روی خودِ `<select>` پنهان گذاشته می‌شود و رویداد از آن
   | فرستاده می‌شود — نه یک شیء دست‌ساز. این‌طور `e.target` واقعاً یک
   | `HTMLSelectElement` است، پس `e.target.name` و `e.target.value` و
   | هر چیز دیگری که مصرف‌کننده بخواند درست است.
   */
  const commit = useCallback(
    (next: string | number) => {
      const nextValue = String(next)

      if (!isControlled) setUncontrolled(nextValue)

      const node = selectRef.current

      if (node) {
        /*
         | ⚠️ مقدار باید با گذر از setter نمونه‌ی React ست شود، وگرنه
         | React تغییر را «قبلاً دیده‌ام» فرض می‌کند و رویداد را
         | نادیده می‌گیرد — همان مشکل شناخته‌شده‌ی ست‌کردن مستقیم
         | `node.value` روی ورودی‌های کنترل‌شده.
         */
        const setter = Object.getOwnPropertyDescriptor(
          window.HTMLSelectElement.prototype,
          'value',
        )?.set

        setter?.call(node, nextValue)
        node.dispatchEvent(new Event('change', { bubbles: true }))
      }
    },
    [isControlled],
  )

  /* کدام نسخه: کشو یا پنجره شناور. */
  useEffect(() => {
    const query = window.matchMedia(`(max-width: ${DRAWER_BREAKPOINT - 1}px)`)
    const sync = () => setIsDrawer(query.matches)

    sync()
    query.addEventListener('change', sync)

    return () => query.removeEventListener('change', sync)
  }, [])

  /*
   | جای پنجره شناور — زیر دکمه، مگر آنکه پایین صفحه جا نباشد.
   |
   | ⚠️ `useLayoutEffect` و نه `useEffect`: با Effect معمولی، پنجره یک
   | فریم در مختصات (۰,۰) گوشه صفحه رندر می‌شد و بعد سرِ جایش می‌پرید.
   */
  useLayoutEffect(() => {
    if (!open || isDrawer) return

    const place = () => {
      const anchor = triggerRef.current?.getBoundingClientRect()
      if (!anchor) return

      const listHeight = listRef.current?.offsetHeight ?? 0
      const below = window.innerHeight - anchor.bottom - VIEWPORT_MARGIN
      const flip = listHeight > below && anchor.top > below

      setPosition({
        top: flip ? anchor.top - listHeight - 4 : anchor.bottom + 4,
        left: anchor.left,
        width: anchor.width,
      })
    }

    place()

    /*
     | اسکرولِ **بیرون** از فهرست، پنجره را می‌بندد.
     |
     | همان رفتار `<select>` بومی: تا وقتی فهرست باز است صفحه سرِ جایش
     | می‌ماند، و هر تکانی بیرونِ فهرست یعنی کاربر کارِ دیگری دارد.
     |
     | 🔴 ولی اسکرولِ خودِ فهرست استثناست. شنونده `capture` است، پس
     | اسکرولِ داخلی هم به اینجا می‌رسد؛ بدون این بررسی، فهرستِ بلند
     | با نخستین چرخشِ غلتک بسته می‌شد و عملاً غیرقابل استفاده بود.
     |
     | ⚠️ `capture` باید بماند: اسکرولِ ظرف‌های داخلی (بدنه Card، نوار
     | فیلتر) روی `window` حباب نمی‌کند و بدون آن اصلاً شنیده نمی‌شود —
     | یعنی پنجره باز می‌ماند و از دکمه جدا می‌افتاد.
     */
    const onScroll = (event: Event) => {
      if (listRef.current && event.target instanceof Node && listRef.current.contains(event.target)) {
        return
      }

      setOpen(false)
    }

    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', place)

    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', place)
    }
  }, [open, isDrawer, items.length])

  /*
   | گزینه‌ای که مکان‌نما رویش است، داخل دید نگه داشته می‌شود.
   |
   | 🔴 فهرست بلند فقط ~۸ سطر را نشان می‌دهد. بدون این، از سطر نهم به
   | بعد مکان‌نما زیرِ لبه می‌رفت و کاربرِ صفحه‌کلید فهرستی می‌دید که
   | تکان نمی‌خورد — انگار ArrowDown کار نمی‌کند.
   |
   | ⚠️ `block: 'nearest'` و نه `center`: فقط وقتی اسکرول می‌کند که
   | گزینه واقعاً بیرون از دید باشد. با `center` هر ضربه کلید کلِ فهرست
   | را می‌پراند، حتی وقتی گزینه جلوی چشم است.
   */
  useEffect(() => {
    if (!open || activeIndex < 0) return

    listRef.current
      ?.querySelector(`#${CSS.escape(`${listboxId}-${activeIndex}`)}`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [open, activeIndex, listboxId])

  /*
   | باز که شد، فهرست بی‌درنگ فوکوس می‌گیرد.
   |
   | 🔴 `useLayoutEffect` و نه `useEffect`+`setTimeout`. با تایمر، فوکوس
   | یک نوبت از حلقه رویداد دیرتر می‌نشست؛ اگر کاربر ArrowDown را نگه
   | داشته یا سریع دوباره بزند، ضربه بعدی هنوز به **دکمه** می‌رسید و نه
   | به فهرست — و چون دکمه فقط «باز کن» بلد است، آن ضربه بی‌اثر گم
   | می‌شد و مکان‌نما تکان نمی‌خورد. یک‌درمیان، بسته به اینکه ضربه زودتر
   | برسد یا تایمر.
   |
   | ⚠️ چون پیش از رنگ‌آمیزی اجرا می‌شود، گره فهرست همان لحظه در DOM
   | هست و فوکوس قطعی است.
   */
  /*
   | ⚠️ در دسکتاپ تا وقتی `position` حساب نشده، پنجره
   | `visibility: hidden` است و گرهِ نامرئی **فوکوس نمی‌گیرد** — بی‌صدا.
   | پس شرطِ اجرا به آماده‌بودنِ جای پنجره گره خورده، نه فقط به `open`.
   | (کشو چنین دروازه‌ای ندارد و همان بار اول فوکوس می‌گیرد.)
   */
  const placed = isDrawer || position !== null

  useLayoutEffect(() => {
    if (!open || !placed) return

    listRef.current?.focus()

    /* در کشو، اسکرول پس‌زمینه قفل می‌شود — مثل Dialog. */
    const { overflow } = document.body.style
    if (isDrawer) document.body.style.overflow = 'hidden'

    return () => {
      if (isDrawer) document.body.style.overflow = overflow
    }
  }, [open, isDrawer, placed])

  /* بستن با کلیک بیرون — فقط در حالت پنجره شناور؛ کشو پرده خودش را دارد. */
  useEffect(() => {
    if (!open || isDrawer) return

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node

      if (listRef.current?.contains(target) || triggerRef.current?.contains(target)) return

      setOpen(false)
    }

    document.addEventListener('mousedown', onPointerDown)

    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open, isDrawer])

  /** حرکت مکان‌نما با پرش از روی گزینه‌های غیرفعال. */
  const move = (from: number, step: number): number => {
    for (let i = from + step; i >= 0 && i < items.length; i += step) {
      if (!items[i].disabled) return i
    }

    return from
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      setOpen(false)
      triggerRef.current?.focus()
      return
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((index) =>
        move(index === -1 ? (event.key === 'ArrowDown' ? -1 : items.length) : index,
          event.key === 'ArrowDown' ? 1 : -1),
      )
      return
    }

    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault()
      setActiveIndex(event.key === 'Home' ? move(-1, 1) : move(items.length, -1))
      return
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()

      const option = items[activeIndex]
      if (!option || option.disabled) return

      commit(option.value)
      setOpen(false)
      triggerRef.current?.focus()
    }
  }

  const openList = () => {
    if (disabled) return

    /*
     | مکان‌نما همین‌جا روی گزینه فعلی می‌نشیند و نه در `useEffect`.
     |
     | 🔴 با Effect یک وقفه‌ی یک‌فریمی می‌ماند که در آن مکان‌نما هنوز
     | `-1` است. کاربری که سریع ArrowDown را دو بار می‌زند، ضربه دوم را
     | روی مقدار کهنه می‌زند و بعد Effect آن را به عقب برمی‌گرداند —
     | یعنی یک پرش که فقط گاهی رخ می‌دهد.
     */
    const atValue = items.findIndex((option) => String(option.value) === String(current ?? ''))

    /*
     | ⚠️ اگر هنوز چیزی انتخاب نشده، مکان‌نما روی نخستین گزینه‌ی
     | قابل‌انتخاب می‌نشیند و نه روی `-1`. با `-1` فهرست باز می‌شد بی‌آنکه
     | هیچ سطری نشانه‌گذاری شود، و کاربرِ صفحه‌کلید نمی‌دانست از کجا
     | شروع کرده — `aria-activedescendant` هم خالی می‌ماند.
     */
    setActiveIndex(atValue >= 0 ? atValue : move(-1, 1))
    setOpen(true)
  }

  /*
   | باز کردن با کلید — و بلعیدنِ همان ضربه.
   |
   | 🔴 بدون `stopPropagation`، یک بار فشردنِ ArrowDown روی دکمه دو کار
   | می‌کرد: فهرست را باز می‌کرد و — چون فهرست بلافاصله فوکوس می‌گیرد —
   | گاهی همان ضربه به `onKeyDown` فهرست هم می‌رسید و مکان‌نما را یک
   | پله جلو می‌بُرد. «گاهی»، چون به ترتیبِ نشستنِ فوکوس و commit شدنِ
   | state بستگی داشت؛ همان چیزی که آزمون را یک‌درمیان می‌شکست.
   |
   | ⚠️ فقط باز می‌کند و مکان‌نما را جابه‌جا نمی‌کند: `openList` خودش
   | آن را روی گزینه فعلی می‌گذارد و کاربر از همان‌جا حرکت می‌کند.
   */
  const onTriggerKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    openList()
  }

  const rows = (
    <div
      ref={listRef}
      role="listbox"
      id={listboxId}
      tabIndex={-1}
      aria-label={title ?? placeholder ?? 'انتخاب گزینه'}
      aria-activedescendant={activeIndex >= 0 ? `${listboxId}-${activeIndex}` : undefined}
      onKeyDown={onKeyDown}
      className={cn(
        'flex flex-col overflow-y-auto focus:outline-none',
        isDrawer
          ? 'max-h-[60dvh] px-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]'
          : cn(
              /* فهرستِ دسکتاپ از زیرِ فیلد باز می‌شود (‎.pf-pop-enter‎)؛ کشوی گوشی حرکتِ خودش را دارد */
              'pf-pop-enter origin-top rounded-[var(--pf-radius-lg)] border border-[var(--pf-border)] p-1',
              'bg-[var(--pf-surface)] shadow-[var(--pf-shadow-xl)] max-h-[16rem]',
            ),
      )}
      style={
        isDrawer
          ? undefined
          : {
              position: 'fixed',
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              width: position?.width,
              /*
                🔴 بالای مدال می‌نشیند.

                ⚠️ پیش‌تر اصلاً `zIndex` نداشت: چون به `body` منتقل
                می‌شود، لایه‌اش «خودکار» بود و زیر مدال (۵۰) می‌افتاد.
                نتیجه‌اش این بود که فهرستِ «نوع واحد» داخل پنجرهٔ
                ثبت، پشت پردهٔ محو دیده می‌شد — باز بود ولی خوانده
                نمی‌شد.
              */
              zIndex: 'var(--pf-z-popover)',
              /* تا پیش از جای‌گیری دیده نشود — وگرنه یک فریم در گوشه صفحه می‌پرد. */
              visibility: position ? 'visible' : 'hidden',
            }
      }
    >
      {items.length === 0 && (
        <p className="px-3 py-6 text-center text-[length:var(--pf-text-sm)] text-[var(--pf-text-muted)]">
          گزینه‌ای نیست.
        </p>
      )}

      {items.map((option, index) => {
        const isSelected = String(option.value) === String(current ?? '')
        const isActive = index === activeIndex

        return (
          <button
            key={option.value}
            type="button"
            role="option"
            id={`${listboxId}-${index}`}
            aria-selected={isSelected}
            disabled={option.disabled}
            /*
              ⚠️ روی `mousedown` جلوی پیش‌فرض گرفته می‌شود تا فوکوس از
              فهرست بیرون نپرد؛ وگرنه کلیکِ ماوس اول فهرست را می‌بندد و
              بعد `click` به گزینه‌ای که دیگر آنجا نیست نمی‌رسد.
            */
            onMouseDown={(event) => event.preventDefault()}
            onMouseEnter={() => !option.disabled && setActiveIndex(index)}
            onClick={() => {
              commit(option.value)
              setOpen(false)
              triggerRef.current?.focus()
            }}
            className={cn(
              /*
                ⚠️ `relative` برای نوارِ کنارِ گزینه فعال لازم است.
              */
              'relative flex w-full items-center justify-between gap-2 text-start',
              'transition-colors duration-[var(--pf-duration-fast)]',
              isDrawer
                ? 'min-h-12 rounded-[var(--pf-radius-lg)] px-3 py-3 text-[length:var(--pf-text-md)]'
                : 'rounded-[var(--pf-radius)] px-3 py-2 text-[length:var(--pf-text-sm)]',
              isSelected
                /* --pf-link و نه --pf-primary — این متن است و روی سطح
                   روشن باید کنتراست بگیرد؛ --pf-primary حالا رنگ
                   پس‌زمینه دکمه است. */
                ? 'font-[var(--pf-weight-bold)] text-[var(--pf-link)]'
                : 'text-[var(--pf-text)]',
              isActive && !option.disabled && 'bg-[var(--pf-primary-soft)]',
              option.disabled && 'cursor-not-allowed opacity-40',
            )}
          >
            {/*
              🔴 نوارِ باریکِ کنارِ گزینه‌ای که مکان‌نما رویش است.

              پس‌زمینه‌ی `--pf-primary-soft` در پوسته روشن بسیار کم‌رنگ
              است (#e6f9f3) و کاربری که با صفحه‌کلید حرکت می‌کند عملاً
              نمی‌دید کجاست. رنگ توکن عوض نشد چون جاهای دیگر هم از آن
              استفاده می‌کنند؛ به‌جایش این نشانه‌ی پرکنتراست اضافه شد.
            */}
            {isActive && !option.disabled && (
              <span
                className="absolute inset-y-1 start-0 w-0.5 rounded-full bg-[var(--pf-primary)]"
                aria-hidden="true"
              />
            )}

            <span className="truncate">{option.label}</span>

            {isSelected && (
              <svg viewBox="0 0 20 20" fill="none" className="size-4 shrink-0" aria-hidden="true">
                <path
                  d="m4 10 4 4 8-8"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        )
      })}
    </div>
  )

  return (
    <div className="relative">
      {/*
        ⚠️ `<select>` پنهان — منبع رسمی مقدار برای فرم بومی و هدفِ
        رویدادِ `change`. با `aria-hidden` و `tabIndex={-1}` از دسترس
        صفحه‌خوان و Tab بیرون است تا دو بار خوانده نشود؛ نقشِ
        دسترس‌پذیری را دکمه و فهرست بازی می‌کنند.
      */}
      <select
        ref={(node) => {
          selectRef.current = node

          if (typeof ref === 'function') ref(node)
          else if (ref) ref.current = node
        }}
        name={name}
        required={required}
        disabled={disabled}
        value={current}
        /*
          ⚠️ همیشه `onChange` می‌گیرد، حتی وقتی مصرف‌کننده نداده باشد.
          `value` بدون `onChange` از نظر React یعنی فیلد فقط‌خواندنی و
          هشدار می‌دهد — و در حالت کنترل‌نشده مصرف‌کننده عمداً چیزی
          نمی‌دهد. تابع تهی هشدار را می‌بَرد بی‌آنکه رفتاری عوض شود.
        */
        onChange={onChange ?? (() => {})}
        aria-hidden="true"
        tabIndex={-1}
        className="sr-only"
      >
        <option value="">{placeholder ?? ''}</option>
        {items.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      <button
        ref={triggerRef}
        type="button"
        id={controlId}
        disabled={disabled}
        /*
          ⚠️ ویژگی‌های باقی‌مانده **پیش از** رویدادها پخش می‌شوند تا
          `onClick`/`onKeyDown`ی که این کامپوننت لازم دارد آخر بنشیند.
          برعکسش یعنی یک `onClick` از بیرون، بازشدن فهرست را بی‌صدا
          از کار بیندازد.
        */
        {...passthrough}
        onClick={openList}
        onKeyDown={onTriggerKeyDown}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-invalid={isInvalid || undefined}
        aria-describedby={field?.describedBy}
        className={cn(
          controlBase(isInvalid),
          'flex h-9.5 items-center justify-between gap-2 pe-3 text-start cursor-pointer',
          /* ⚠️ کلاسِ hover اینجا حذف شد: حالا خودِ `controlBase` آن را
             دارد و دو کلاسِ `hover:bg-*` روی یک عنصر یعنی برنده‌شان
             ترتیبِ خروجی Tailwind است، نه ترتیبِ نوشتنشان. */
          /* فهرستِ باز = فیلدِ فعال؛ خطِ زیرین هم مثل حالت فوکوس سبز می‌ماند. */
          open && !isInvalid && 'border-b-[var(--pf-primary-container)]',
          className,
        )}
      >
        <span
          className={cn(
            'truncate',
            selected ? 'text-[var(--pf-text)]' : 'text-[var(--pf-text-subtle)]',
          )}
        >
          {selected?.label ?? placeholder ?? ''}
        </span>

        <svg
          className={cn(
            'size-4 shrink-0 text-[var(--pf-text-subtle)] transition-transform',
            open && 'rotate-180',
          )}
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="m6 8 4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open &&
        createPortal(
          isDrawer ? (
            /* ---------- کشوی موبایل ---------- */
            <div
              className={cn(
                'fixed inset-0 flex items-end justify-center',
                /*
                  ⚠️ حین بسته‌شدن دیگر کلیک نمی‌گیرد. آن ۲۰۰ میلی‌ثانیه
                  گره هنوز سرِ جایش است و بدون این، کاربر می‌توانست روی
                  گزینه‌ای بزند که دارد از صفحه بیرون می‌رود.
                */
                closing && 'pointer-events-none',
              )}
              /* ⚠️ کشو هم باید بالای مدال باشد، نه هم‌ترازش. */
              style={{ zIndex: 'var(--pf-z-popover)' }}
            >
              <div
                className="absolute inset-0"
                style={{
                  backgroundColor:
                    'color-mix(in srgb, var(--pf-scrim) var(--pf-scrim-opacity), transparent)',
                  backdropFilter: 'blur(var(--pf-scrim-blur))',
                  WebkitBackdropFilter: 'blur(var(--pf-scrim-blur))',
                  /* پرده محو می‌شود و کشو سُر می‌خورد — با هم شروع و تمام می‌شوند. */
                  animation: `${closing ? 'pf-fade-out' : 'pf-fade-in'} ${DRAWER_EXIT_MS}ms var(--pf-ease) both`,
                }}
                onClick={() => setOpen(false)}
                aria-hidden="true"
              />

              <div
                role="dialog"
                aria-modal="true"
                aria-label={title ?? placeholder ?? 'انتخاب گزینه'}
                className={cn(
                  'relative flex w-full flex-col overflow-hidden',
                  'rounded-t-[var(--pf-radius-xl)] bg-[var(--pf-surface)]',
                  'shadow-[var(--pf-shadow-xl)] max-h-[85dvh]',
                )}
                /*
                  ⚠️ `both` لازم است: بدون آن، کشو در فاصله‌ی میانِ پایانِ
                  انیمیشن و برداشته‌شدنِ گره، یک فریم به جای اولش
                  برمی‌گشت — یعنی درست همان پرشی که می‌خواستیم نباشد.
                */
                style={{
                  animation: `${closing ? 'pf-drawer-out' : 'pf-drawer-in'} ${DRAWER_EXIT_MS}ms var(--pf-ease) both`,
                }}
              >
                {/* دستگیره — نشانه‌ی آشنای کشوی قابل‌بستن. */}
                <div className="flex shrink-0 justify-center pt-3" aria-hidden="true">
                  <span className="h-1 w-9 rounded-full bg-[var(--pf-border-strong)]" />
                </div>

                {/* دکمهٔ بستن سمتِ راست (پیش از عنوان) — همان قاعدهٔ Dialog */}
                <div className="flex shrink-0 items-center gap-3 px-5 py-3">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    aria-label="بستن"
                    className="-ms-1.5 flex size-8 shrink-0 items-center justify-center rounded-[var(--pf-radius)] text-[var(--pf-text-muted)] transition-colors hover:bg-[var(--pf-surface-hover)] hover:text-[var(--pf-text)]"
                  >
                    <svg viewBox="0 0 20 20" fill="none" className="size-4" aria-hidden="true">
                      <path
                        d="m5 5 10 10M15 5 5 15"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                  <h2 className="min-w-0 flex-1 truncate text-[length:var(--pf-text-base)] font-[var(--pf-weight-bold)] text-[var(--pf-text)]">
                    {title ?? placeholder ?? 'انتخاب گزینه'}
                  </h2>

                </div>

                {rows}
              </div>
            </div>
          ) : (
            rows
          ),
          document.body,
        )}
    </div>
  )
})

/* ------------------------------------------------------------------
   Checkbox / Radio
   ------------------------------------------------------------------ */

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode
  hint?: ReactNode
}

/* چک‌باکس با جعبهٔ دست‌سازِ خودمان — نه رندرِ پیش‌فرضِ مرورگر.

   پیش‌تر یک input بومی با accent-color بود. مشکلش این بود که شکلِ
   جعبه، گردیِ گوشه، ضخامتِ خط و اندازهٔ تیک را مرورگر تعیین می‌کرد،
   پس نه با --pf-radius هم‌خوان بود، نه با ضخامتِ حاشیهٔ بقیهٔ کنترل‌ها،
   و در هر مرورگر/سیستم‌عامل کمی فرق می‌کرد. حالتِ hover هم نداشت.

   حالا input نامرئی می‌شود (appearance-none) ولی سرِ جایش می‌ماند —
   حذف نمی‌شود تا دسترس‌پذیری و رفتار بومی (Space، فرم، :focus-visible)
   دست‌نخورده بماند — و تیک به‌صورت SVG رویش کشیده می‌شود.

   ⚠️ زوجِ رنگِ حالتِ تیک‌خورده عمداً --pf-primary / --pf-on-primary است،
   نه --pf-link. این دو با هم تعریف شده‌اند («متن تیره روی سبز روشن») و
   در هر دو پوسته همان جفت می‌مانند. اگر زمینه را --pf-link می‌گذاشتیم،
   در پوستهٔ روشن آن سبزِ تیره (accent-800) می‌شد و تیکِ تیره
   (accent-950) رویش عملاً نامرئی — فقط در پوستهٔ تاریک درست درمی‌آمد. */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, hint, className, ...rest },
  ref,
) {
  const id = useId()
  return (
    <div className={cn('flex items-start gap-2.5', className)}>
      {/* ظرفِ هم‌اندازهٔ جعبه تا تیکِ absolute جای دقیق بگیرد.

          mt جعبه را با خطِ اولِ برچسب هم‌تراز می‌کند و عددش حساب‌شده
          است، نه سرانگشتی: خطِ اول ۰٫۸۷۵rem در ۱٫۷ (--pf-leading-normal)
          یعنی ۱٫۴۹rem، جعبه ۱٫۱۲۵rem؛ نصفِ اختلاف می‌شود ~۰٫۱۸rem.
          با items-start و بدون این، جعبه بالاتر از متن می‌نشست. */}
      <span className="relative mt-[0.18rem] grid size-[1.125rem] shrink-0 place-items-center">
        <input
          ref={ref}
          id={id}
          type="checkbox"
          className={cn(
            'peer size-full cursor-pointer appearance-none rounded-[var(--pf-radius-sm)]',
            'border border-[var(--pf-border-strong)] bg-[var(--pf-surface)]',
            'transition-colors duration-[var(--pf-duration-fast)]',
            'hover:border-[var(--pf-primary)]',
            'checked:border-[var(--pf-primary)] checked:bg-[var(--pf-primary)]',
            'disabled:cursor-not-allowed disabled:opacity-60',
            'disabled:hover:border-[var(--pf-border-strong)]',
          )}
          {...rest}
        />

        {/* تیک فقط وقتی input تیک خورده دیده می‌شود.
            pointer-events-none تا کلیک همیشه به خودِ input برسد. */}
        <svg
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute size-3 text-[var(--pf-on-primary)]',
            'scale-75 opacity-0 transition-[opacity,transform] duration-[var(--pf-duration-fast)]',
            'peer-checked:scale-100 peer-checked:opacity-100',
          )}
        >
          <path
            d="M2.5 8.5 6 12l7.5-8"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>

      <label htmlFor={id} className="cursor-pointer select-none">
        <span className="block text-[length:var(--pf-text-md)] text-[var(--pf-text)]">{label}</span>
        {hint && (
          <span className="mt-0.5 block text-[length:var(--pf-text-xs)] text-[var(--pf-text-muted)]">
            {hint}
          </span>
        )}
      </label>
    </div>
  )
})
